import { useId, useState } from 'react';

import { formatNombre } from '../../lib/format';

export interface Categorie {
  libelle: string;
  /** Couleur de la série (variable CSS de la palette validée). */
  couleur: string;
}

export interface Jour {
  date: string;
  /** Une valeur par catégorie, dans l'ordre des catégories (bas de la pile en premier). */
  valeurs: number[];
}

const LARGEUR = 900;
const HAUTEUR = 180;
const MARGE_G = 44;
const MARGE_B = 22;
const ECART = 2;

/** Graduation « ronde » au-dessus du maximum : 0, 25, 50… */
export function echelle(max: number): number[] {
  if (max <= 0) return [0, 1];
  const brut = max / 3;
  const puissance = 10 ** Math.floor(Math.log10(brut));
  const pas = [1, 2, 2.5, 5, 10].map((m) => m * puissance).find((p) => p >= brut) ?? brut;
  const haut = Math.ceil(max / pas) * pas;
  return Array.from({ length: Math.round(haut / pas) + 1 }, (_, i) => Math.round(i * pas * 100) / 100);
}

/** Chemin d'une colonne : haut arrondi (4 px), base carrée. */
function colonne(x: number, y: number, l: number, h: number, arrondi: boolean): string {
  const r = arrondi ? Math.min(4, h, l / 2) : 0;
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + l - r}Q${x + l},${y} ${x + l},${y + r}V${y + h}Z`;
}

/**
 * Colonnes par jour, empilées quand il y a plusieurs catégories (cahier des
 * charges §7.10). Un seul axe, colonnes de 24 px au plus séparées par un
 * espace, légende dès deux séries, info-bulle au survol ou au clavier.
 */
export function ColonnesJour({
  jours,
  categories,
  libelle,
  format = formatNombre,
}: {
  jours: Jour[];
  categories: Categorie[];
  libelle: string;
  format?: (n: number) => string;
}) {
  const id = useId();
  const [survol, setSurvol] = useState<number | null>(null);
  const totaux = jours.map((j) => j.valeurs.reduce((a, b) => a + b, 0));
  const graduations = echelle(Math.max(0, ...totaux));
  const haut = graduations[graduations.length - 1]!;
  const zone = HAUTEUR - MARGE_B;
  const pas = (LARGEUR - MARGE_G) / Math.max(1, jours.length);
  const largeur = Math.min(24, Math.max(2, pas - 2));
  const y = (v: number) => zone - (v / haut) * (zone - 6);
  const etiquettes = jours.length <= 10 ? 1 : jours.length <= 31 ? 7 : 15;
  const j = survol !== null ? jours[survol] : null;
  // Info-bulle centrée sur la colonne, recalée sur les bords pour ne pas sortir du cadre.
  const position = survol !== null ? ((MARGE_G + survol * pas + pas / 2) / LARGEUR) * 100 : 0;

  return (
    <div className="viz">
      {categories.length > 1 && (
        <ul className="viz__legende" aria-label="Légende">
          {categories.map((c) => (
            <li key={c.libelle}>
              <span className="viz__pastille" style={{ background: c.couleur }} aria-hidden="true" />
              {c.libelle}
            </li>
          ))}
        </ul>
      )}
      <div className="viz__cadre" onMouseLeave={() => setSurvol(null)}>
        <svg className="viz__svg" viewBox={`0 0 ${LARGEUR} ${HAUTEUR}`} role="img" aria-labelledby={`${id}-titre`}>
          <title id={`${id}-titre`}>{libelle}</title>
          {graduations.map((g) => (
            <g key={g}>
              <line className="viz__grille" x1={MARGE_G} x2={LARGEUR} y1={y(g)} y2={y(g)} />
              <text className="viz__axe" x={MARGE_G - 6} y={y(g) + 3} textAnchor="end">
                {format(g)}
              </text>
            </g>
          ))}
          {jours.map((jour, i) => {
            const x = MARGE_G + i * pas + (pas - largeur) / 2;
            let base = zone;
            const dernier = jour.valeurs.reduce((d, v, k) => (v > 0 ? k : d), -1);
            return (
              <g key={jour.date} className={survol === i ? 'viz__jour viz__jour--actif' : 'viz__jour'}>
                {jour.valeurs.map((v, k) => {
                  if (v <= 0) return null;
                  const h = Math.max(1, zone - y(v) - (k < dernier ? ECART : 0));
                  const haut_ = base - h;
                  const chemin = colonne(x, haut_, largeur, h, k === dernier);
                  base = haut_ - (k < dernier ? ECART : 0);
                  return <path key={k} d={chemin} fill={categories[k]?.couleur} />;
                })}
                {i % etiquettes === 0 && (
                  <text className="viz__axe" x={x + largeur / 2} y={HAUTEUR - 6} textAnchor="middle">
                    {jour.date.slice(8, 10)}/{jour.date.slice(5, 7)}
                  </text>
                )}
                <rect
                  className="viz__cible"
                  x={MARGE_G + i * pas}
                  y={0}
                  width={pas}
                  height={zone}
                  tabIndex={0}
                  aria-label={`${jour.date.split('-').reverse().join('/')} : ${categories
                    .map((c, k) => `${c.libelle} ${format(jour.valeurs[k] ?? 0)}`)
                    .join(', ')}`}
                  onMouseEnter={() => setSurvol(i)}
                  onFocus={() => setSurvol(i)}
                  onBlur={() => setSurvol(null)}
                />
              </g>
            );
          })}
          <line className="viz__base" x1={MARGE_G} x2={LARGEUR} y1={zone} y2={zone} />
        </svg>
        {j && survol !== null && (
          <div
            className="viz__bulle"
            role="status"
            style={{ left: `${position}%`, transform: `translateX(${position > 80 ? -100 : position < 20 ? 0 : -50}%)` }}
          >
            <div className="viz__bulle-date">{j.date.split('-').reverse().join('/')}</div>
            {categories.map((c, k) => (
              <div key={c.libelle} className="viz__bulle-ligne">
                <span className="viz__pastille" style={{ background: c.couleur }} aria-hidden="true" />
                <span>{c.libelle}</span>
                <strong>{format(j.valeurs[k] ?? 0)}</strong>
              </div>
            ))}
            {categories.length > 1 && (
              <div className="viz__bulle-ligne viz__bulle-total">
                <span />
                <span>Total</span>
                <strong>{format(totaux[survol] ?? 0)}</strong>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
